import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { get } from '../../lib/apiClient';
import { cn } from '@vd/ui';
import { AdminTable, type AdminTableColumn, useFocusParam } from './AdminTable';
import { Sparkline } from './Sparkline';
import { planClass } from './planBadge';
import styles from './Admin.module.css';
import { formatDay } from '../../lib/time';

/**
 * Admin → Accounts.
 *
 * One row per company. Sort + search via the shared AdminTable; the
 * filter slot holds a plan dropdown so staff can narrow by tier
 * (free / personal / team / business / enterprise). Each row expands into the
 * member grid in a sub-row; member name+email clicks navigate to
 * /admin/users?focus=<userId>.
 *
 * Usage-based pricing (TOADO-655): what the add-on subscription renews, the
 * widget tier bought, and the MRR split into plan, add-ons and tier.
 *
 * Focus query param (`?focus=<companyId>`) auto-expands the target
 * row when navigating in from the Users page.
 */
interface AdminCompanyMember {
  id: string;
  email: string;
  name: string;
  role: string;
  joinedAt: string;
  lastActiveAt: string | null;
}

interface AdminCompanyRow {
  id: string;
  name: string;
  plan: 'free' | 'personal' | 'team' | 'business' | 'enterprise';
  planInterval: 'monthly' | 'annual' | null;
  subscriptionStatus: string;
  seatsPurchased: number;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  memberCount: number;
  members: AdminCompanyMember[];
  ticketsThisMonth: number;
  ticketQuotaOverride: number | null;
  quotaOverrideReason: string | null;
  planOverrideAt: string | null;
  createdAt: string;
  /** Staff's widget allowances in place of the plan's; null follows the plan. */
  visitorMinutesOverride: number | null;
  widgetReportsOverride: number | null;
  /** What the add-on subscription renews: units, or seats for video history. */
  addonItems: Record<string, number>;
  addonSubscriptionStatus: string | null;
  widgetTier: 'starter' | 'growth' | 'scale' | null;
  widgetTierStatus: string | null;
  widgetTierInterval: 'monthly' | 'annual' | null;
  widgetTierEndsAtPeriodEnd: boolean;
  /** The tier the plan comes with (Business: Starter), from the server's rule. */
  widgetTierIncluded: 'starter' | 'growth' | 'scale' | null;
  /** The whole estimate: plan, add-ons and widget tier. */
  mrrCents: number | null;
  planMrrCents: number | null;
  addonMrrCents: number;
  tierMrrCents: number;
  dailyCreated30: number[];
}

const ADDON_LABELS: Array<[key: string, label: string]> = [
  ['narration_boost', 'Narration'],
  ['recording_boost', 'Recording'],
  ['video_history', 'Video history'],
];

/**
 * "Narration x2, Recording x1, Video history", or "None". Video history is
 * per seat, so its count is the seat count and is left out of the summary.
 * An add-on this build does not know still shows, by its key.
 */
export function addonsSummary(items: Record<string, number>): string {
  const known = new Set(ADDON_LABELS.map(([key]) => key));
  const parts: string[] = [];
  for (const [key, label] of ADDON_LABELS) {
    const units = items[key] ?? 0;
    if (units <= 0) continue;
    parts.push(key === 'video_history' ? label : `${label} x${units}`);
  }
  for (const [key, units] of Object.entries(items)) {
    if (!known.has(key) && units > 0) parts.push(`${key} x${units}`);
  }
  return parts.length === 0 ? 'None' : parts.join(', ');
}

/** Stripe statuses a widget tier is in use under, and the ones it is over under. */
const TIER_LIVE = new Set(['active', 'trialing', 'past_due']);
const TIER_ENDED = new Set(['canceled', 'incomplete_expired']);

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

interface AdminCompaniesResponse {
  companies: AdminCompanyRow[];
  total: number;
  limit: number;
  offset: number;
}

type PlanFilter = 'all' | 'free' | 'personal' | 'team' | 'business' | 'enterprise';

export function AccountsPage() {
  const focus = useFocusParam();
  const [planFilter, setPlanFilter] = useState<PlanFilter>('all');
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());

  // Auto-expand the focused row when arriving via ?focus=.
  useEffect(() => {
    if (focus) {
      setOpenIds((prev) => {
        if (prev.has(focus)) return prev;
        const next = new Set(prev);
        next.add(focus);
        return next;
      });
    }
  }, [focus]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: () => get<AdminCompaniesResponse>('/admin/companies'),
    staleTime: 30_000,
  });

  const filteredRows = (data?.companies ?? []).filter((c) => planFilter === 'all' || c.plan === planFilter);
  const totalMrrCents = filteredRows.reduce((acc, c) => acc + (c.mrrCents ?? 0), 0);

  function toggleOpen(id: string) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const columns: AdminTableColumn<AdminCompanyRow>[] = [
    {
      key: 'expand',
      label: '',
      render: (c) => {
        const open = openIds.has(c.id);
        const Chevron = open ? ChevronDown : ChevronRight;
        return (
          <button
            type="button"
            className={styles.expandBtn}
            onClick={(e) => { e.stopPropagation(); toggleOpen(c.id); }}
            aria-expanded={open}
            aria-label={open ? 'Collapse members' : 'Expand members'}
          >
            <Chevron size={12} />
          </button>
        );
      },
    },
    {
      key: 'account',
      label: 'Account',
      sortable: true,
      sortAccessor: (r) => r.name.toLowerCase(),
      render: (c) => (
        <div className={styles.userCell}>
          <span className={styles.userName}>{c.name}</span>
          <span className={styles.userEmail}>{c.id}</span>
        </div>
      ),
    },
    {
      key: 'plan',
      label: 'Plan',
      sortable: true,
      sortAccessor: (r) => r.plan,
      render: (c) => (
        <>
          <span className={cn(styles.planBadge, planClass(c.plan))}>{c.plan}</span>
          {c.planOverrideAt && (
            <span className={styles.overrideBadge} title={`Set manually on ${formatDate(c.planOverrideAt)}`}>comp</span>
          )}
          {c.ticketQuotaOverride != null && (
            <span className={styles.overrideBadge} title={c.quotaOverrideReason ?? 'Quota override set'}>
              {c.ticketQuotaOverride}/mo
            </span>
          )}
        </>
      ),
    },
    {
      key: 'seats',
      label: 'Seats',
      center: true,
      sortable: true,
      sortAccessor: (r) => r.seatsPurchased,
      render: (c) => c.seatsPurchased,
    },
    {
      key: 'members',
      label: 'Members',
      center: true,
      sortable: true,
      sortAccessor: (r) => r.memberCount,
      render: (c) => c.memberCount,
    },
    {
      key: 'tix',
      label: 'Tickets this mo.',
      center: true,
      sortable: true,
      sortAccessor: (r) => r.ticketsThisMonth,
      render: (c) => c.ticketsThisMonth,
    },
    {
      key: 'spark',
      label: '30d tickets',
      title: 'Tickets created in the last 30 days',
      sortable: true,
      sortAccessor: (r) => r.dailyCreated30.reduce((a, b) => a + b, 0),
      render: (c) => {
        const total = c.dailyCreated30.reduce((a, b) => a + b, 0);
        if (total === 0) return <span className={styles.muted}>-</span>;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkline values={c.dailyCreated30} label="tickets created" />
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)', minWidth: 16 }}>{total}</span>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      sortAccessor: (r) => r.subscriptionStatus,
      render: (c) => (
        <span className={styles.muted}>
          {c.subscriptionStatus}
          {c.cancelAtPeriodEnd ? ' (cancel pending)' : ''}
        </span>
      ),
    },
    {
      key: 'addons',
      label: 'Add-ons',
      title: 'What the add-on subscription renews each month',
      sortable: true,
      sortAccessor: (r) => r.addonMrrCents,
      render: (c) => {
        const summary = addonsSummary(c.addonItems);
        return (
          <span className={summary === 'None' ? styles.muted : undefined}>
            {summary}
            {c.addonSubscriptionStatus === 'past_due' && <span className={styles.overrideBadge}>past due</span>}
          </span>
        );
      },
    },
    {
      key: 'tier',
      label: 'Widget tier',
      sortable: true,
      sortAccessor: (r) => r.tierMrrCents,
      render: (c) => {
        const included = c.widgetTierIncluded
          ? <span className={styles.muted}>{capitalise(c.widgetTierIncluded)} (in plan)</span>
          : null;
        if (!c.widgetTier || !c.widgetTierStatus) return included ?? <span className={styles.muted}>-</span>;
        const name = capitalise(c.widgetTier);
        if (TIER_ENDED.has(c.widgetTierStatus)) return included ?? <span className={styles.muted}>{name}, ended</span>;
        if (!TIER_LIVE.has(c.widgetTierStatus)) {
          return <span className={styles.muted} title={`Subscription ${c.widgetTierStatus}`}>{name}, not in use</span>;
        }
        return (
          <span>
            {name}
            {c.widgetTierInterval && <span className={styles.muted}> ({c.widgetTierInterval})</span>}
            {c.widgetTierEndsAtPeriodEnd && <span className={styles.overrideBadge} title="Set to end with its period">ending</span>}
            {c.widgetTierStatus === 'past_due' && <span className={styles.overrideBadge}>past due</span>}
          </span>
        );
      },
    },
    {
      key: 'mrr',
      label: 'MRR',
      title: 'Plan, add-ons and widget tier, at list price',
      sortable: true,
      sortAccessor: (r) => r.mrrCents ?? -1,
      render: (c) => {
        if (c.mrrCents == null) return <span className={styles.muted}>-</span>;
        const split = `Plan ${c.planMrrCents == null ? '-' : dollars(c.planMrrCents)}, add-ons ${dollars(c.addonMrrCents)}, tier ${dollars(c.tierMrrCents)}`;
        return (
          <div title={split}>
            {dollars(c.mrrCents)}/mo
            {(c.addonMrrCents > 0 || c.tierMrrCents > 0) && (
              <div className={styles.userEmail}>{split}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'created',
      label: 'Created',
      sortable: true,
      sortAccessor: (r) => r.createdAt,
      render: (c) => <span className={styles.muted}>{formatDate(c.createdAt)}</span>,
    },
  ];

  const navigate = useNavigate();

  return (
    <>
      <h1 className={styles.h1}>Accounts</h1>
      <p className={styles.sub}>
        Every company in the system. Sortable + searchable; filter by plan
        tier. Click the chevron to expand the row and see members.
        {data ? ` ~$${(totalMrrCents / 100).toFixed(0)}/mo MRR (plans, add-ons and widget tiers) across the visible set.` : ''}
      </p>

      {error ? (
        <p style={{ color: 'var(--danger)' }}>
          Failed to load accounts: {error instanceof Error ? error.message : 'unknown error'}
        </p>
      ) : isLoading || !data ? (
        <p className={styles.muted}>Loading…</p>
      ) : (
        <AdminTable<AdminCompanyRow>
          columns={columns}
          rows={filteredRows}
          getRowId={(c) => c.id}
          searchTextFor={(c) => `${c.name} ${c.id} ${c.members.map((m) => `${m.name} ${m.email}`).join(' ')}`}
          searchPlaceholder="Search by name, id, or member…"
          initialSortKey="created"
          initialSortDir="desc"
          highlightRowId={focus}
          emptyText={planFilter === 'all' ? 'No accounts.' : `No ${planFilter} accounts.`}
          filterBar={(visible, total) => (
            <>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                Plan
                <select
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value as PlanFilter)}
                  style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', color: 'inherit' }}
                >
                  <option value="all">All</option>
                  <option value="free">Free</option>
                  <option value="personal">Personal</option>
                  <option value="team">Team</option>
                  <option value="business">Business</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </label>
              {planFilter !== 'all' && (
                <span className={styles.muted} style={{ fontSize: 11 }}>
                  {visible} of {total} accounts
                </span>
              )}
            </>
          )}
        />
      )}

      {/* Expanded member grids render BELOW the table as separate sections
          rather than as colSpanned sub-rows. This sidesteps the
          AdminTable's strict row/column model and keeps the member-card
          links clickable without fighting the table's row-click handler. */}
      {(data?.companies ?? [])
        .filter((c) => openIds.has(c.id) && (planFilter === 'all' || c.plan === planFilter))
        .map((c) => (
          <div key={c.id} className={styles.section}>
            <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)', marginTop: 0 }}>
              {c.name} members
            </h3>
            {c.members.length === 0 ? (
              <span className={styles.muted}>No members.</span>
            ) : (
              <div className={styles.membersGrid}>
                {c.members.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={styles.memberCard}
                    onClick={() => navigate(`/admin/users?focus=${encodeURIComponent(m.id)}`)}
                    style={{ cursor: 'pointer', textAlign: 'left' }}
                    title={`Open ${m.name} in Users`}
                  >
                    <div className={styles.memberName}>
                      {m.name}
                      {m.role === 'owner' ? <span className={styles.staffBadge}>OWNER</span> : null}
                    </div>
                    <div className={styles.userEmail}>{m.email}</div>
                    <div className={styles.memberMeta}>
                      {m.role} · joined {formatDate(m.joinedAt)}
                      {m.lastActiveAt ? ` · last active ${formatDate(m.lastActiveAt)}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
    </>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return formatDay(d);
  } catch {
    return '-';
  }
}
